/**
 * Dynamic community fill — pure functions ported from the legacy monolith
 * (__planImg, __dynCityOf, __dynItemsForLevel, __dynRowsForLevel,
 * __dynHeroList, __dynCardTitle/__dynTableTitle/__dynTableCols).
 *
 * These turn community content packs (/sfmc-content) + a chosen level
 * (comms | plans | homes) into card ITEMS (Featured block), table ROWS
 * (Listing block, pipe-delimited string its renderer parses), or HERO images.
 *
 * STANDING PRODUCT RULE (Jack, repeated): switching a section to Dynamic must
 * NOT auto-fill — fills happen only on an explicit user pick. Callers enforce
 * that; these functions just compute.
 */
import type { CommunityPackResponse, PlanInfo, HomesiteInfo } from "../types/api";
import type { CardItem } from "../email/helpers";
import { echCityState } from "./cityState";

export interface Pack {
  name: string;
  code: string;
  C: CommunityPackResponse;
}

export type FillLevel = "comms" | "plans" | "homes";

/**
 * THE LISTING CAP IS 15, NOT 10 (corrected 2026-08-20 while porting the picker).
 *
 * The monolith declares `var DYN_MAX_HOMES=15` (index.html L9555, comment: "listing
 * table is text rows (no images) — allow up to 15") and uses that ONE constant for
 * all three jobs: the picker's homes cap, the selected-tile cap, and the row cap in
 * `__dynRowsForLevel`. This file had 10, which is `DYN_MAX_LOTS` — a DIFFERENT
 * monolith constant (L9556) counting the production Dynamic_ListingTable's ten
 * @lotID AMPscript slots. The two were conflated at port time, so a Homesite-level
 * listing silently dropped rows 11–15 that the monolith emits.
 */
export const DYN_MAX_HOMES = 15;

const num = (v: unknown): string => (v ? Number(String(v).replace(/[^0-9.]/g, "")).toLocaleString() : "");

/** legacy __planImg — real photos first, the SVG floorplan diagram LAST, skip placeholders */
export function planImg(pl?: PlanInfo): string {
  if (!pl) return "";
  const bad = (u?: string) => !u || /comingsoon|coming[-_]soon|placeholder|no[-_]?image|default/i.test(u);
  const ok = (u?: string) => (bad(u) ? "" : (u as string));
  return ok(pl.hero) || ok(pl.elevation) || ok(pl.kitchen) || ok(pl.floorplan) || pl.hero || pl.elevation || pl.kitchen || pl.floorplan || "";
}

/** legacy __dynCityOf */
export function cityOf(p: Pack, x?: { city?: string; state?: string; whcCity?: string; whcState?: string }): string {
  const c = (x && (x.city || x.whcCity)) || "";
  const st = (x && (x.state || x.whcState)) || "";
  /* never "Saratoga Springs, UT, UT" (2026-10-30, Justin, SLC 1041310) — see cityState.ts */
  return c ? echCityState(c, st) : p.name || "";
}

/**
 * ── AVAILABILITY IS THE GATE, NOT "NOT SOLD" (2026-08-22) ─────────────────────────
 * Jack: "for homes count make sure the status is available more than anything."
 *
 * The old rule was the monolith's `__notSold` (index.html L9559) — anything whose
 * status did not literally say "sold" counted. That is a hole: PENDING, UNDER
 * CONTRACT, RESERVED, ON HOLD and MODEL HOME all pass a not-sold test while being
 * homes a requester must not feature, and a community that shows "8 homes" has to
 * mean eight a buyer can actually buy.
 *
 * MEASURED, 2026-08-22 — 26 JAX communities / 89 homes off `/sfmc-content`: the only
 * two statuses in the live packs are `MOVE_IN_READY` (55) and `UNDER_CONSTRUCTION`
 * (34). No sold rows at all, so the worker's feed is already availability-scoped and
 * this predicate is the guard for the day it stops being.
 *
 * The rules, in order:
 *   1. blank status → counts. The feed is an availability feed; a missing label is
 *      not a reason to drop a real home.
 *   2. anything in the UNAVAILABLE list → never counts, whatever else it says.
 *   3. a recognised availability word → counts.
 *   4. an unrecognised status → does NOT count. Strict on purpose, per Jack. A new
 *      status that means "available" belongs in AVAILABLE below — add it there
 *      rather than loosening this.
 */
const UNAVAILABLE = /sold|pending|contract|reserv|on hold|withdraw|off market|coming soon|model home|unavailab|not avail|closed/i;
const AVAILABLE = /avail|move ?in|ready|under construction|quick move|to be built|spec\b|for sale/i;
export const isAvailable = (hm: HomesiteInfo): boolean => {
  /* SFMC ships these as MOVE_IN_READY / UNDER_CONSTRUCTION, so underscores and
     hyphens become spaces before either list is applied — matching on the raw token
     silently failed UNDER_CONSTRUCTION and would have dropped 34 of 89 JAX homes. */
  const s = String(hm.status || "").trim().replace(/[_\-/|]+/g, " ").replace(/\s+/g, " ");
  if (!s) return true;
  if (UNAVAILABLE.test(s)) return false;
  return AVAILABLE.test(s);
};
/** kept as the old name so every call site reads the same; availability is the test now */
export const notSold = isAvailable;
export const homeKey = (code: string, hm: HomesiteInfo) => code + "|h|" + (hm.addr || hm.plan || "") + "|" + (hm.price || "");

export function cardTitle(level: FillLevel): string {
  return ({ comms: "Featured Communities", plans: "Featured Floorplans", homes: "Featured Homes" } as const)[level] || "Featured";
}
export function tableTitle(level: FillLevel): string {
  return ({ comms: "Explore Communities", plans: "Explore Floorplans", homes: "Explore Homes" } as const)[level] || "Explore Homes";
}
export function tableCols(level: FillLevel): [string, string][] {
  if (level === "comms") return [["Community", "left"], ["City", "center"], ["View", "center"]];
  if (level === "plans") return [["Floorplan", "left"], ["Sq Ft / Beds / Baths", "center"], ["View", "center"]];
  return [["Address", "left"], ["Sq Ft / Beds / Baths / Garage", "center"], ["View", "center"]];
}

/**
 * CARD ITEMS for the Featured block, per level — legacy __dynItemsForLevel.
 * `pickedPlans` / `pickedHomes` are the user's explicit picks (plan key =
 * "<code>|p|<index>", home key = homeKey()); empty picks fall back to the
 * legacy defaults (first 6 / round-robin).
 */
export function itemsForLevel(
  packs: Pack[],
  level: FillLevel,
  picks: { plans?: string[]; homes?: string[] } = {},
): CardItem[] {
  const items: CardItem[] = [];
  if (level === "comms") {
    packs.forEach((p) => {
      const cm = p.C.community || ({} as NonNullable<CommunityPackResponse["community"]>);
      items.push({ img: cm.hero || "", name: p.name, desc: cityOf(p, cm), sqft: "", beds: "", baths: "", link: "View Community", url: cm.url || "#" });
    });
    return items.slice(0, 6);
  }
  if (level === "homes") {
    const picked = picks.homes || [];
    const pool: { p: Pack; hm: HomesiteInfo; key: string }[] = [];
    packs.forEach((p) => (p.C.homesites || []).filter(notSold).forEach((hm) => pool.push({ p, hm, key: homeKey(p.code, hm) })));
    const chosen = picked.length ? pool.filter((x) => picked.indexOf(x.key) > -1) : pool.slice(0, 6);
    chosen.slice(0, 6).forEach((x) => {
      const hm = x.hm;
      items.push({
        img: hm.img || "", name: hm.addr || hm.plan || "Home", desc: cityOf(x.p, hm),
        sqft: num(hm.sqft) ? num(hm.sqft) + " sq ft" : "", beds: hm.beds ? hm.beds + " Beds" : "", baths: hm.baths ? hm.baths + " Baths" : "",
        link: "View Home", url: hm.url || "#",
      });
    });
    return items;
  }
  /* plans (default) */
  const pk = picks.plans || [];
  if (pk.length) {
    pk.slice(0, 6).forEach((k) => {
      const pp = k.split("|");
      const pack = packs.find((x) => x.code === pp[0]);
      const arr = pack ? pack.C.plans || [] : [];
      const pl = arr[+pp[2]];
      if (pl && pack)
        items.push({
          img: planImg(pl) || "", name: pl.name || "Plan", desc: pack.name,
          sqft: num(pl.sqft) ? num(pl.sqft) + " sq ft" : "", beds: pl.beds ? pl.beds + " Beds" : "", baths: pl.baths ? pl.baths + " Baths" : "",
          link: "View Floorplan", url: pl.url || "#",
        });
    });
  } else {
    let idx = 0;
    while (items.length < 6) {
      let added = false;
      for (let i = 0; i < packs.length && items.length < 6; i++) {
        const arr2 = packs[i].C.plans || [];
        const pl2 = arr2[idx];
        if (pl2) {
          items.push({
            img: planImg(pl2) || "", name: pl2.name || "Plan", desc: packs[i].name,
            sqft: num(pl2.sqft) ? num(pl2.sqft) + " sq ft" : "", beds: pl2.beds ? pl2.beds + " Beds" : "", baths: pl2.baths ? pl2.baths + " Baths" : "",
            link: "View Floorplan", url: pl2.url || "#",
          });
          added = true;
        }
      }
      if (!added) break;
      idx++;
    }
  }
  return items;
}

export interface TableRowsResult {
  title: string;
  cols: [string, string][];
  /** pipe-delimited string the Listing renderer parses */
  rowsStr: string;
  viewUrl: string;
  n: number;
}

/** TABLE ROWS for the Listing block, per level — legacy __dynRowsForLevel */
export function rowsForLevel(packs: Pack[], level: FillLevel): TableRowsResult {
  const lines: string[] = [];
  let n = 0;
  const viewUrl = (packs[0] && packs[0].C.community && packs[0].C.community.url) || "#";
  if (level === "comms") {
    packs.forEach((p) => {
      const cm = p.C.community || ({} as NonNullable<CommunityPackResponse["community"]>);
      lines.push(p.name + " | " + (cityOf(p, cm) || "—") + " | View Community" + (cm.url ? " | " + cm.url : ""));
      n++;
    });
  } else if (level === "plans") {
    const byC: Record<string, PlanInfo[]> = {};
    const order: string[] = [];
    packs.forEach((p) => (p.C.plans || []).forEach((pl) => { if (!byC[p.name]) { byC[p.name] = []; order.push(p.name); } byC[p.name].push(pl); }));
    order.forEach((c) => {
      lines.push(c);
      byC[c].forEach((pl) => {
        if (n >= DYN_MAX_HOMES) return;
        lines.push((pl.name || "Plan") + " | " + (num(pl.sqft) || "—") + " / " + (pl.beds || "—") + " / " + (pl.baths || "—") + " | View Floorplan" + (pl.url ? " | " + pl.url : ""));
        n++;
      });
    });
  } else {
    let all: { h: HomesiteInfo; city: string }[] = [];
    packs.forEach((p) => (p.C.homesites || []).forEach((hm) => all.push({ h: hm, city: cityOf(p, hm) })));
    all = all.slice(0, DYN_MAX_HOMES);
    const byCity: Record<string, HomesiteInfo[]> = {};
    const ord: string[] = [];
    all.forEach((x) => { if (!byCity[x.city]) { byCity[x.city] = []; ord.push(x.city); } byCity[x.city].push(x.h); });
    ord.forEach((c) => {
      lines.push(c);
      byCity[c].forEach((hm) => {
        lines.push((hm.addr || hm.plan || "Home") + " | " + (num(hm.sqft) || "—") + " / " + (hm.beds || "—") + " / " + (hm.baths || "—") + (hm.garage ? " / " + hm.garage : "") + " | View Home" + (hm.url ? " | " + hm.url : ""));
        n++;
      });
    });
  }
  return { title: tableTitle(level), cols: tableCols(level), rowsStr: lines.join("\n"), viewUrl, n };
}

/** HERO image list — legacy __dynHeroList. heroKeys = "<code>|hero" picks in order. */
export function heroList(packs: Pack[], heroKeys: string[]): { url: string; name: string }[] {
  const out: { url: string; name: string }[] = [];
  if (heroKeys.length) {
    heroKeys.forEach((k) => {
      const code = k.split("|")[0];
      const p = packs.find((x) => x.code === code);
      if (p && p.C.community && p.C.community.hero) out.push({ url: p.C.community.hero, name: p.name });
    });
  } else {
    const p0 = packs.find((p) => p.C.community && p.C.community.hero);
    if (p0) out.push({ url: p0.C.community!.hero, name: p0.name });
  }
  return out;
}
